'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  value: string;
}

interface DataFilterBarProps {
  onFilterChange: (filters: FilterState) => void;
}

interface FilterState {
  region: string;
  timePeriod: string;
  threatType: string;
  searchQuery: string;
}

const DataFilterBar = ({ onFilterChange }: DataFilterBarProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    region: 'all',
    timePeriod: '2025',
    threatType: 'all',
    searchQuery: ''
  });
  const [isExpanded, setIsExpanded] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const regions: FilterOption[] = [
    { id: 'all', label: 'All Regions', value: 'all' },
    { id: 'kruger', label: 'Kruger National Park', value: 'kruger' },
    { id: 'hluhluwe', label: 'Hluhluwe-iMfolozi', value: 'hluhluwe' },
    { id: 'etosha', label: 'Etosha National Park', value: 'etosha' },
    { id: 'lewa', label: 'Lewa Wildlife Conservancy', value: 'lewa' },
    { id: 'ol-pejeta', label: 'Ol Pejeta Conservancy', value: 'ol-pejeta' }
  ];

  const timePeriods: FilterOption[] = [
    { id: '2026', label: '2026', value: '2026' },
    { id: '2025', label: '2025', value: '2025' },
    { id: '2024', label: '2024', value: '2024' },
    { id: '2023', label: '2023', value: '2023' },
    { id: '2020-2025', label: '2020-2025', value: '2020-2025' },
    { id: '2015-2020', label: '2015-2020', value: '2015-2020' }
  ];

  const threatTypes: FilterOption[] = [
    { id: 'all', label: 'All Threats', value: 'all' },
    { id: 'poaching', label: 'Poaching', value: 'poaching' },
    { id: 'habitat-loss', label: 'Habitat Loss', value: 'habitat-loss' },
    { id: 'human-conflict', label: 'Human-Wildlife Conflict', value: 'human-conflict' },
    { id: 'disease', label: 'Disease', value: 'disease' },
    { id: 'climate', label: 'Climate Change', value: 'climate' }
  ];

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    if (!isHydrated) return;
    
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleReset = () => {
    if (!isHydrated) return;
    
    const resetFilters: FilterState = {
      region: 'all',
      timePeriod: '2025',
      threatType: 'all',
      searchQuery: ''
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-md p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-headline font-bold text-foreground">Data Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="h-10 bg-muted rounded-md animate-pulse"></div>
          <div className="h-10 bg-muted rounded-md animate-pulse"></div>
          <div className="h-10 bg-muted rounded-md animate-pulse"></div>
          <div className="h-10 bg-muted rounded-md animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md p-4 lg:p-6 shadow-organic-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-headline font-bold text-foreground">Data Filters</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="lg:hidden p-2 text-muted-foreground hover:text-primary transition-organic duration-300"
          aria-label="Toggle filters"
        >
          <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={20} variant="outline" />
        </button>
      </div>

      <div className={`space-y-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        {/* Search Bar */}
        <div className="relative">
          <Icon 
            name="MagnifyingGlassIcon" 
            size={20} 
            variant="outline" 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search conservation data..."
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-md text-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-organic duration-300"
          />
        </div>

        {/* Filter Dropdowns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Region Filter */}
          <div>
            <label className="block text-sm font-body font-medium text-foreground mb-2">
              Region
            </label>
            <select
              value={filters.region}
              onChange={(e) => handleFilterChange('region', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-md text-sm font-body text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-organic duration-300"
            >
              {regions.map((region) => (
                <option key={region.id} value={region.value}>
                  {region.label}
                </option>
              ))}
            </select>
          </div>

          {/* Time Period Filter */}
          <div>
            <label className="block text-sm font-body font-medium text-foreground mb-2">
              Time Period
            </label>
            <select
              value={filters.timePeriod}
              onChange={(e) => handleFilterChange('timePeriod', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-md text-sm font-body text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-organic duration-300"
            >
              {timePeriods.map((period) => (
                <option key={period.id} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>

          {/* Threat Type Filter */}
          <div>
            <label className="block text-sm font-body font-medium text-foreground mb-2">
              Threat Type
            </label>
            <select
              value={filters.threatType}
              onChange={(e) => handleFilterChange('threatType', e.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-input rounded-md text-sm font-body text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-organic duration-300"
            >
              {threatTypes.map((threat) => (
                <option key={threat.id} value={threat.value}>
                  {threat.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Reset Button */}
        <div className="flex justify-end">
          <button
            onClick={handleReset}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-body font-medium text-muted-foreground hover:text-primary hover:bg-muted rounded-md transition-organic duration-300"
          >
            <Icon name="ArrowPathIcon" size={18} variant="outline" />
            <span>Reset Filters</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataFilterBar;